import type { PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { buildClassName } from './buildClassName';
import { DrivingSpeedReadout } from './DrivingSpeedReadout';
import type { DrivingAction, DrivingActionState } from '../hooks/useDrivingInput';
import type { VehicleState } from '../model/vehicleDriving';

interface PedalEntry {
  action: DrivingAction;
  label: string;
  ariaLabel: string;
  modifier?: string;
}

/**
 * 조향은 왼쪽, 가감속은 오른쪽에 둡니다. 양손 엄지로 잡는 배치이고, 실제 게임 패드와
 * 같은 관례라 처음 봐도 어느 쪽이 무엇인지 짐작할 수 있습니다.
 */
const STEER_ENTRIES: PedalEntry[] = [
  { action: 'left', label: '◀', ariaLabel: '왼쪽으로 조향' },
  { action: 'right', label: '▶', ariaLabel: '오른쪽으로 조향' },
];

const PEDAL_ENTRIES: PedalEntry[] = [
  { action: 'brake', label: '제동', ariaLabel: '브레이크', modifier: 'driving-controls__button--brake' },
  { action: 'reverse', label: '후진', ariaLabel: '후진' },
  { action: 'throttle', label: '가속', ariaLabel: '가속', modifier: 'driving-controls__button--throttle' },
];

export interface DrivingControlsProps {
  actions: DrivingActionState;
  onPress: (action: DrivingAction) => void;
  onRelease: (action: DrivingAction) => void;
  /** 속도 표시가 읽는 운동 상태. 3D 루프가 씁니다. */
  stateRef: RefObject<VehicleState>;
  /** 계기판이 0을 가리킬 때 대신 띄울 문구. 그대로 DrivingSpeedReadout에 넘깁니다. */
  idleLabel?: string;
}

/**
 * 화면 조작 패드와 속도 표시.
 *
 * 왜 click이 아니라 포인터 이벤트인가:
 *   주행은 "누르고 있는 동안" 가속해야 합니다. click은 뗄 때 한 번 오므로 쓸 수 없습니다.
 *
 * pointerdown에서 setPointerCapture를 하는 이유:
 *   버튼을 누른 채 손가락이 밖으로 미끄러지면 pointerup이 다른 요소로 갑니다. 그러면
 *   가속이 눌린 채로 남아 차가 저절로 달립니다. 캡처해 두면 뗄 때 이 버튼이 받습니다.
 *
 * 키보드로도 같은 조작이 되지만(useDrivingInput이 직접 듣습니다) 버튼에 포커스가 갈 수
 * 있어야 해서 button 요소를 씁니다. 다만 Enter/Space로는 "누르고 있기"가 안 되므로,
 * 키보드 사용자는 방향키·WASD 쪽을 쓰게 됩니다 — 그 안내가 idleLabel입니다.
 *
 * 속도 표시를 여기 둔 이유:
 *   조향과 페달을 양쪽 끝으로 벌리면 가운데가 비는데, 그 폭이 폰에서 100px이 넘습니다.
 *   속도를 따로 한 줄 잡으면 뷰어 세로를 40px 더 먹고, 좁은 세로 배치에서 그만큼이
 *   차가 보일 자리입니다. 성능상 손해도 없습니다 — DrivingSpeedReadout이 자기 rAF로
 *   자기만 다시 렌더하므로, 부모가 무엇이든 갱신 비용은 그 span 하나입니다.
 *
 *   한 줄에 나란히 놓아서 이 바의 높이는 버튼 하나 높이(44px)로 유지됩니다. 속도를
 *   위에, 다른 것을 아래에 쌓으면 바가 79px로 두꺼워져서 뷰어를 그만큼 가립니다.
 *
 * 키보드 안내를 이 바 안에 한 줄로 붙이지 않는 이유:
 *   붙여 봤고, 그 줄만으로 19px을 먹었습니다. 지금은 정지 중일 때만 속도 자리에
 *   대신 띄우므로(idleLabel) 세로를 한 픽셀도 더 쓰지 않습니다. 달리기 시작하면
 *   안내는 사라지고 그 자리에 속도가 들어옵니다 — 그때는 안내가 필요 없습니다.
 *
 * "출발선으로"는 여기 없습니다. 누르고 있는 조작이 아니라 차량을 되돌리는 동작이라
 * 보기/주행 세그먼트와 같은 줄에 있습니다(ShowroomPage). 여기 두면 이 바가 두꺼워지고,
 * 후진 중 표시("후진 18 km/h")까지 들어가면 폰에서 한 줄에 담기지 않습니다.
 */
export function DrivingControls({
  actions,
  onPress,
  onRelease,
  stateRef,
  idleLabel,
}: DrivingControlsProps) {
  const renderButton = (entry: PedalEntry) => {
    const isActive = actions[entry.action];
    const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
      event.currentTarget.setPointerCapture(event.pointerId);
      onPress(entry.action);
    };
    const stop = () => onRelease(entry.action);

    return (
      <button
        key={entry.action}
        type="button"
        className={buildClassName(
          entry.modifier === undefined
            ? 'driving-controls__button'
            : `driving-controls__button ${entry.modifier}`,
          'driving-controls__button--active',
          isActive
        )}
        aria-label={entry.ariaLabel}
        aria-pressed={isActive}
        onPointerDown={handlePointerDown}
        onPointerUp={stop}
        // 포인터가 취소되는 경우(시스템 제스처, 전화 수신 등)에도 입력을 놓아야 합니다.
        onPointerCancel={stop}
        onLostPointerCapture={stop}
      >
        {entry.label}
      </button>
    );
  };

  return (
    <div className="driving-controls" role="group" aria-label="주행 조작">
      <div className="driving-controls__cluster driving-controls__cluster--steer">
        {STEER_ENTRIES.map(renderButton)}
      </div>

      <DrivingSpeedReadout stateRef={stateRef} idleLabel={idleLabel} />

      <div className="driving-controls__cluster driving-controls__cluster--pedals">
        {PEDAL_ENTRIES.map(renderButton)}
      </div>
    </div>
  );
}
